import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Star, FileText, CheckCircle, XCircle, Eye, BookOpen } from 'lucide-react';
import { topicsAPI, applicationsAPI } from '../lib/api';
import { LoadingPage, Modal, Badge } from '../components/shared';
import toast from 'react-hot-toast';

export default function TopicApplicationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: '', review_notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      topicsAPI.getApplications(id),
      topicsAPI.getOne(id)
    ])
      .then(([appsRes, topicRes]) => {
        setApplications(appsRes.data);
        setTopic(topicRes.data);
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleReview = async () => {
    if (!reviewForm.status) return;
    setSubmitting(true);
    try {
      await applicationsAPI.review(reviewing.id, reviewForm);
      toast.success(`Application ${reviewForm.status}`);
      setReviewing(null);
      setReviewForm({ status: '', review_notes: '' });
      const res = await topicsAPI.getApplications(id);
      setApplications(res.data);
    } catch {
      toast.error('Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingPage />;

  const pending = applications.filter(a => a.status === 'pending').length;
  const accepted = applications.filter(a => a.status === 'accepted').length;

  const STATUS_COLOR = {
    pending: 'var(--warning)', under_review: 'var(--info)',
    accepted: 'var(--success)', rejected: 'var(--error)'
  };

  return (
    <div className="page-container">
      <button
        className="btn btn-ghost"
        style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}
        onClick={() => navigate('/dashboard/topics')}
      >
        <ArrowLeft size={16} /> Back to My Topics
      </button>

      <div className="page-header">
        <div>
          <h1 className="page-title">{topic?.title}</h1>
          <p className="page-subtitle">Review and respond to student applications</p>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        {[
          { label: 'Total Applications', value: applications.length, color: 'var(--text-primary)' },
          { label: 'Pending Review', value: pending, color: 'var(--warning)' },
          { label: 'Accepted', value: accepted, color: 'var(--success)' },
          { label: 'Slots', value: `${topic?.filled_slots || 0} / ${topic?.student_slots || 1}`, color: 'var(--teal)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '14px 20px', flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 600, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {applications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          <FileText size={48} style={{ opacity: 0.3, margin: '0 auto 16px', display: 'block' }} />
          No applications received yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {applications.map(app => (
            <div key={app.id} className="card" style={{ padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'rgba(45,212,191,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.85rem', fontWeight: 600, color: 'var(--teal)',
                      flexShrink: 0
                    }}>
                      {app.first_name?.[0]}{app.last_name?.[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {app.first_name} {app.last_name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: 10, marginTop: 2 }}>
                        <span>{app.matric_number}</span>
                        <span>{app.department}</span>
                        <span>{app.level}</span>
                        {app.gpa && (
                          <span style={{ color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Star size={10} /> {app.gpa}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {app.skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                      {app.skills.slice(0, 6).map(s => (
                        <span key={s} style={{ fontSize: '0.7rem', padding: '2px 8px', background: 'rgba(45,212,191,0.08)', color: 'var(--teal)', borderRadius: 4 }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Statement preview */}
                  <div style={{
                    fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.55,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                  }}>
                    {app.statement_of_interest}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 20, alignItems: 'flex-end' }}>
                  <span style={{
                    fontSize: '0.7rem', fontFamily: 'var(--font-mono)',
                    color: STATUS_COLOR[app.status] || 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.08em'
                  }}>
                    {app.status.replace(/_/g, ' ')}
                  </span>

                  {app.cv_url && (
                    <a href={`/${app.cv_url}`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '5px 12px' }}>
                      <BookOpen size={12} /> CV
                    </a>
                  )}

                  {['pending', 'under_review'].includes(app.status) && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '5px 14px' }}
                      onClick={() => {
                        setReviewing(app);
                        setReviewForm({ status: '', review_notes: '' });
                      }}
                    >
                      <Eye size={13} /> Review
                    </button>
                  )}
                </div>
              </div>

              {app.review_notes && (
                <div style={{
                  marginTop: 12, padding: '8px 12px',
                  background: 'rgba(255,255,255,0.03)', borderRadius: 6,
                  fontSize: '0.8rem', color: 'var(--text-muted)',
                  borderLeft: `3px solid ${STATUS_COLOR[app.status] || 'var(--text-muted)'}`
                }}>
                  Your note: {app.review_notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      <Modal open={!!reviewing} onClose={() => setReviewing(null)} title="Review Application">
        {reviewing && (
          <div className="modal-body">
            <div style={{ marginBottom: 20, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{reviewing.first_name} {reviewing.last_name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{reviewing.matric_number} · {reviewing.department} · {reviewing.level}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Statement of Interest</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, maxHeight: 160, overflowY: 'auto' }}>
                {reviewing.statement_of_interest}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Decision *</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { value: 'under_review', label: 'Mark Under Review', icon: Eye, color: 'var(--info)' },
                  { value: 'accepted', label: 'Accept', icon: CheckCircle, color: 'var(--success)' },
                  { value: 'rejected', label: 'Reject', icon: XCircle, color: 'var(--error)' },
                ].map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    onClick={() => setReviewForm(f => ({ ...f, status: value }))}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 8, border: `2px solid ${reviewForm.status === value ? color : 'rgba(255,255,255,0.08)'}`,
                      background: reviewForm.status === value ? `${color}18` : 'transparent',
                      color: reviewForm.status === value ? color : 'var(--text-muted)',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, transition: 'all 0.15s'
                    }}
                  >
                    <Icon size={16} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Note to Student (optional)</label>
              <textarea
                className="input"
                rows={3}
                placeholder="Provide feedback or reasoning for your decision..."
                value={reviewForm.review_notes}
                onChange={e => setReviewForm(f => ({ ...f, review_notes: e.target.value }))}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReviewing(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleReview}
                disabled={submitting || !reviewForm.status}
              >
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
